// External Imports
import { useEffect, useState, useContext, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { MdAdd, MdCheck, MdClose, MdEdit, MdRefresh } from "react-icons/md";
import { Tb360View, TbChevronDown, TbChevronUp } from "react-icons/tb";

// Internal Imports
import { DataContext } from "../../context/data-context";
import { uploadImageToFirebase } from "../../util/upload-image-to-firebase";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import { toTitleCase } from "../../util/format-text";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import VehicleStatus from "./VehicleStatus";
import SelectPair from "../UI/SelectPair";
import ImageSelector from "../UI/ImageSelector";
import DetailList from "../UI/DetailList";
import VehicleFlags from "./VehicleFlags";
import PersonCard from "../UI/PersonCard";
import Notes from "../UI/Notes";
import { UtilityContext } from "../../context/util-context";
import VehiclesRevenue from "./VehiclesRevenue";
import LoadingSpinner from "../UI/LoadingSpinner";
import TagSelector from "../UI/TagSelector";
import AuditLogs from "../UI/AuditLogs";

/** ========================================================================
  ** Vehicle Information Page
  This page displays all information about a vehicle.
 * ========================================================================== */

export default function VehicleInfo() {
  // Get User From Context
  const { locations } = useContext(DataContext);
  const { navigate } = useContext(UtilityContext);

  // Get Vehicle ID from URL
  const [searchParms] = useSearchParams();
  const id = searchParms.get("id");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [vehicle, setVehicle] = useState({});

  // =================== FETCH VEHICLE DATA ===================
  const fetchVehicle = useCallback(async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "vehicle",
        id: id,
        columns: [
          "make { id name image }",
          "location { id name }",
          "plan { id name }",
          "booking: activeBooking { id customer { firstName lastName } startDate endDate }",
          "application: applications_on_vehicle { id registeration insurance monthlyPayment dueDate institution partner { name photo phone id }}",
          "bookings: bookings_on_vehicle { dateCreated startDate endDate totalFees customer { firstName lastName } }",
          "createdBy { name photo }",
        ],
      });
      // Set Vehicle Application
      if (response.application.length > 0)
        response.application = response.application[0];
      else response.application = null;

      setVehicle(response);
      setLoading(false);
      try {
        if (response.tollTagId === "requested")
          await requestAdminAPI("get-ntta-vehicle", { id: id });
      } catch (err) {
        console.log(err);
      }
    } catch (err) {
      setLoading(false);
    }
  }, [id]);

  // =================== EDIT VEHICLE ===================
  const editVehicleHandler = async () => {
    try {
      // Get Vehicle ID
      const vehicleId = vehicle.id;

      setLoading(true);
      // Create payload for all vehicle information
      const payload = {
        id: vehicleId,
        vin: vehicle.vin,
        make: vehicle.makeId,
        model: vehicle.model,
        year: vehicle.year,
        trim: document.getElementById("trim").value,
        license: document.getElementById("license")?.value || vehicle.license,
        licenseState:
          document.getElementById("licenseState")?.value ||
          vehicle.licenseState,
        locationId: document.getElementById("locationId").value,
        drive: document.getElementById("drive").value,
        fuel: document.getElementById("fuel").value,
        seats: document.getElementById("seats").value,
        doors: document.getElementById("doors").value,
        color: document.getElementById("color").value,
        plan: document.getElementById("planId").value,
        rate: document.getElementById("rate").value,
        status: document.getElementById("status").value,
        monthlyMiles: document.getElementById("monthlyMiles").value,
        keyTag: document.getElementById("keyTag")?.value || vehicle.keyTag,
        available: document.getElementById("available").value === "Yes",
        type: document.getElementById("type").value?.toLowerCase(),
        description: document.getElementById("description")?.value ? document.getElementById("description").value : vehicle.description || "",
      };

      // Upload only if new image is provided
      const cornerImage = document.getElementById("cornerImage").files[0];
      if (cornerImage)
        payload.cornerImage = await uploadImageToFirebase(
          `images/${vehicleId}/corner`,
          "corner",
          cornerImage,
          () => {}
        );
      else payload.cornerImage = vehicle.cornerImage;

      // Send Payload to API
      await requestAdminAPI("manage-vehicle", payload);

      // Fetch Vehicles and Reset Edit Mode
      fetchVehicle();
      setEdit(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch vehicle data on load
  useEffect(() => {
    if (id) fetchVehicle();
  }, [id]);

  if (!id || (!loading && (!vehicle || !vehicle?.id)))
    return (
      <div className="container">
        <p className="text-stone-400">
          Vehicle ID Not Provided or Vehicle Not Found!
        </p>
      </div>
    );

  return (
    <div className="divider">
      <div className="container gap-4 lg:col-span-3">
        <Heading title="Vehicle Information" id={id}>
          <AuditLogs table="vehicle" id={id} />
          <MdRefresh onClick={fetchVehicle} className="icon" />
        </Heading>

        <div className="grid gap-4 text-sm lg:grid-cols-3">
          <div className="grid gap-4">
            <div className="flex items-center space-x-4">
              <img src={vehicle.make?.image} className="object-contain w-20" />
              <div>
                <p className="font-bold text-stone-200">
                  {vehicle.year} {vehicle.make?.name}
                </p>
                <p className="text-2xl font-bold">{vehicle.model}</p>
                <p className="text-base text-stone-400">{vehicle.vin}</p>
              </div>
            </div>
            <ImageSelector
              loading={loading}
              image={vehicle.cornerImage}
              classes="h-48 lg:w-3/4"
              name="cornerImage"
              edit={edit}
              label="CORNER IMAGE"
            />
          </div>
          <div className="info lg:col-span-2">
            <DetailPair
              loading={loading}
              label="Trim"
              value={vehicle.trim}
              edit={edit}
              inputOptions={{
                required: true,
                id: "trim",
                defaultValue: vehicle.trim,
              }}
            />
            <DetailPair
              loading={loading}
              label="License Plate"
              value={vehicle.license}
              edit={edit && !vehicle.tollTagId}
              inputOptions={{
                required: true,
                id: "license",
                defaultValue: vehicle.license,
              }}
              onClick={() => navigate(`/vehicles/register?id=${vehicle.id}`)}
            />
            <DetailPair
              loading={loading}
              label="License State"
              value={vehicle.licenseState}
              edit={edit && !vehicle.tollTagId}
              inputOptions={{
                required: true,
                id: "licenseState",
                defaultValue: vehicle.licenseState,
              }}
              onClick={() => navigate(`/vehicles/register?id=${vehicle.id}`)}
            />
            <DetailPair
              loading={loading}
              label="Toll Tag ID"
              classes={
                !vehicle.tollTagId
                  ? "text-red-600 animate-pulse"
                  : vehicle.tollTagId === "requested"
                  ? "text-yellow-600"
                  : "text-green-600"
              }
              value={vehicle.tollTagId || "Not Registered"}
              onClick={() => navigate(`/vehicles/register?id=${vehicle.id}`)}
            />
            <SelectPair
              label="Home Location"
              edit={edit}
              value={vehicle.location?.name}
              options={locations?.map((loc) => ({
                id: loc.id,
                name: loc?.name,
              }))}
              loading={loading}
              inputOptions={{
                required: true,
                id: "locationId",
                defaultValue: vehicle.location?.id,
              }}
            />
            <SelectPair
              label="Drive"
              options={["AWD", "RWD", "FWD"]}
              value={vehicle.drive?.toUpperCase()}
              edit={edit}
              loading={loading}
              inputOptions={{
                required: true,
                id: "drive",
                defaultValue: vehicle.drive?.toUpperCase(),
              }}
            />
            <SelectPair
              label="Fuel Type"
              options={["Gasoline", "Hybrid", "Electric", "Plugin-Hybrid"]}
              edit={edit}
              value={vehicle.fuel}
              loading={loading}
              inputOptions={{
                required: true,
                id: "fuel",
                defaultValue: vehicle.fuel,
              }}
            />
            <SelectPair
              label="Body Type"
              options={[
                { name: "Sedan", id: "sedan" },
                { name: "SUV", id: "suv" },
                { name: "Coupe", id: "coupe" },
                { name: "Convertible", id: "convertible" },
                { name: "Hatchback", id: "hatchback" },
                { name: "Truck", id: "truck" },
                { name: "Crossover", id: "crossover" },
              ]}
              edit={edit}
              value={toTitleCase(vehicle.type)}
              inputOptions={{
                id: "type",
                defaultValue: vehicle.type,
                required: true,
              }}
              loading={loading}
            />
            <DetailPair
              loading={loading}
              label="Color"
              value={vehicle.color}
              edit={edit}
              inputOptions={{
                required: true,
                id: "color",
                defaultValue: vehicle.color,
              }}
            />
            <DetailPair
              loading={loading}
              label="Seats"
              edit={edit}
              value={vehicle.seats}
              inputOptions={{
                required: true,
                type: "number",
                id: "seats",
                defaultValue: vehicle.seats,
              }}
            />
            <DetailPair
              loading={loading}
              label="Doors"
              edit={edit}
              value={vehicle.doors}
              inputOptions={{
                required: true,
                type: "number",
                id: "doors",
                defaultValue: vehicle.doors,
              }}
            />
            <SelectPair
              label="Plan"
              edit={edit}
              value={vehicle.plan?.name}
              loading={loading}
              options={["LUXURY", "PLUSH", "POSH"]}
              inputOptions={{
                required: true,
                id: "planId",
                defaultValue: vehicle.plan?.name,
              }}
            />

            <DetailPair
              loading={loading}
              label="Monthly Rate"
              edit={edit}
              value={`$${vehicle.rate}`}
              inputOptions={{
                required: true,
                type: "number",
                step: "0.01",
                id: "rate",
                defaultValue: vehicle.rate,
              }}
            />
            <DetailPair
              loading={loading}
              label="Monthly Miles"
              edit={edit}
              value={`${vehicle.monthlyMiles}mi`}
              inputOptions={{
                required: true,
                type: "number",
                id: "monthlyMiles",
                defaultValue: vehicle.monthlyMiles,
              }}
            />
            <SelectPair
              loading={loading}
              label="Status"
              edit={edit}
              classes={
                vehicle.status == "maintenance" &&
                "text-yellow-600 font-semibold animate-pulse"
              }
              link={
                vehicle.status === "maintenance" &&
                `/maintenance?status=&query=${vehicle.vin}`
              }
              value={toTitleCase(vehicle.status)}
              options={[
                "available",
                "maintenance",
                "paused",
                "sold",
                "totalled",
              ]}
              inputOptions={{
                required: true,
                id: "status",
                defaultValue: vehicle.status,
              }}
            />
            <SelectPair
              label="Available"
              loading={loading}
              edit={edit}
              options={["Yes", "No"]}
              classes={
                vehicle.available
                  ? "text-green-600 font-semibold"
                  : "text-red-600 font-semibold animate-pulse"
              }
              value={vehicle.available ? "Yes" : "No"}
              inputOptions={{
                required: true,
                id: "available",
                defaultValue: vehicle.available ? "Yes" : "No",
              }}
            />
          </div>
        </div>

        <VehicleStatus vin={vehicle.vin} trackerId={vehicle.trackerId} />
        <VehiclesRevenue vehicleId={vehicle.id} />
        <VehicleDescription edit={edit} vehicle={vehicle} />
        <Notes table="vehicle" id={vehicle.id} />
      </div>

      <div className="sidebar">
        <div className="grid gap-6">
          <div className="flex items-center justify-around space-x-4">
            {edit ? (
              <Fragment>
                <MdClose onClick={() => setEdit(false)} className="icon" />
                <MdCheck onClick={editVehicleHandler} className="icon" />
              </Fragment>
            ) : (
              <MdEdit onClick={() => setEdit(true)} className="icon" />
            )}
            <Tb360View
              onClick={() =>
                window.open(
                  `https://icontent.sister.tv/new_media_layer.html?project_id=poshcars_121924&vin=${vehicle.id}&player_type=1`,
                  "_blank"
                )
              }
              className="icon"
            />
          </div>
          <div className="w-full">
            <VehicleFlags vehicle={vehicle} clickable={true} />
          </div>
          {vehicle.application && (
            <PersonCard
              label="Partner:"
              name={vehicle.application.partner?.name}
              secondary={vehicle.application.partner?.phone}
              image={vehicle.application.partner?.photo}
              link={`/partners/info?id=${vehicle.application.partner?.id}`}
              loading={loading}
            />
          )}
          {vehicle.application && (
            <DetailList
              label="Application Details"
              onClick={() =>
                navigate(`/applications/info?id=${vehicle.application?.id}`)
              }
              values={[
                {
                  name: vehicle.application?.monthlyPayment
                    ? `$${vehicle.application?.monthlyPayment}/mo`
                    : "Unknown Monthly Payment",
                  classes: !vehicle.application?.monthlyPayment
                    ? "warning animate-pulse"
                    : "text-lg font-semibold text-yellow-600",
                },
                {
                  name: vehicle.application?.dueDate
                    ? `${isoDateToString(
                        vehicle.application?.dueDate,
                        "do"
                      )} of every month`
                    : "Unknown Due Date",
                  classes: !vehicle.application?.dueDate
                    ? "warning animate-pulse"
                    : "text-stone-400",
                },
                {
                  name:
                    vehicle.application?.institution || "Unknown Institution",
                  classes: !vehicle.application?.institution
                    ? "warning animate-pulse"
                    : "text-stone-400",
                },
              ]}
            />
          )}
          {vehicle.booking && (
            <DetailList
              label="Booking Details"
              link={`/bookings/info?id=${vehicle.booking?.id}`}
              values={[
                `${vehicle.booking?.customer?.firstName} ${vehicle.booking?.customer?.lastName}`,
                `${isoDateToString(
                  vehicle.booking?.startDate
                )} - ${isoDateToString(vehicle.booking?.endDate)}`,
                ,
              ]}
              loading={loading}
            />
          )}
          <TagSelector table="vehicle" id={vehicle?.id} onSelect={fetchVehicle}/>
          <PersonCard
            label="Onboarded By:"
            name={vehicle.createdBy?.name}
            secondary={isoDateToString(vehicle.dateCreated)}
            image={vehicle.createdBy?.photo}
            link={`/employees/info?id=${vehicle.createdById}`}
            loading={loading}
          />
        </div>
        {loading ? (
          <div className="grid gap-4">
            {Array.from({ length: 2 }, (_, i) => (
              <div key={i} className="h-10 btn-secondary animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid gap-4">
            {!vehicle.booking && (
              <div
                className="btn-secondary"
                onClick={(e) =>
                  navigate(`/bookings/add?vehicleId=${vehicle.id}`)
                }
              >
                Add Booking
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ========================================================================
// * DESCRIPTION SECTION
// ==========================================================================
function VehicleDescription({ edit, vehicle }) {
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  /* ======================= GET DESCRIPTION FROM AI ======================= */
  const getAIResponse = async () => {
    try {
      if (!edit) return;
      setLoading(true);
      const response = await requestAdminAPI("get-ai-response", {
        prompt: `Write a 50 word description for an online listing of ${vehicle.year} ${vehicle.color} ${vehicle.make?.name} ${vehicle.model} ${vehicle.trim}, which is included in the ${vehicle.plan?.name} subscription plan.`,
      });
      document.getElementById("description").value = response;
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-4 mb-4">
      <Heading isBackArrow={false} title="Vehicle Description">
        {loading ? (
          <LoadingSpinner classes={"w-5 h-5 text-white"}/>
        ) : (
          edit && (
            <MdRefresh onClick={getAIResponse} className="text-2xl icon" />
          )
        )}
        {show ? (
          <TbChevronUp className="icon" onClick={() => setShow(false)} />
        ) : (
          <TbChevronDown className="icon" onClick={() => setShow(true)} />
        )}
      </Heading>

      {show && <textarea
        className="w-full text-sm form-input text-stone-200"
        rows={6}
        id="description"
        name="description"
        placeholder="Click the refresh icon to get an AI generated description"
        defaultValue={vehicle.description}
        disabled={!edit || loading}
      />}
    </div>
  );
}
